import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import styled, { withTheme } from 'styled-components';

import { Badge } from '@ge/components/badge';
import { Icon, Icons } from '@ge/components/icon';
import { MobileTab, AssetConnectionStatus, EntityType, MobileAssetTab } from '@ge/models/constants';
import { getDateTimeBasedOnZone } from '@ge/shared/util/time-date';
import { getTurbineStateColor, getTurbineStateType } from '@ge/shared/util/turbine-state';
import { globalColors } from '@ge/tokens/colors';
import { elevations } from '@ge/tokens/elevations';
import EntityDetailsWeather from '@ge/web-client/src/app/components/entity-details/entity-details-weather';
import { useSiteRealTimeInfo, useAssetRealTimeData, useSiteDetails } from '@ge/shared/data-hooks';

const SiteDetails = styled.div`
  display: flex;
  margin-top: 6px;
  .turbine-count {
    padding-left: 6px;
  }
`;

const CasePanelSubTitle = styled.h2`
  font-family: GE Inspira;
  font-weight: 300;
  font-size: 22px;
  margin: 0;
  color: ${(props) => props.theme.entityDetails.headerIconColor};
  letter-spacing: 0;
`;
const SiteIcon = styled(Icon).attrs((props) => ({
  size: props.size,
  icon: Icons.SITE,
  color: props.theme.entityDetails.siteHeaderIconColor,
}))``;
const OfflineIcon = styled(Icon).attrs((props) => ({
  size: 12,
  icon: Icons.OFFLINE,
  color: props.theme.entityDetails.siteHeaderIconColor,
}))`
  padding-left: 19px;
  padding-right: 7px;
`;

const ChevronIcon = styled(Icon).attrs((props) => ({
  size: 20,
  icon: Icons.CHEVRON,
  color: props.color || props.theme.entityDetails.headerIconColor,
  rotate: 90,
}))``;

const TurbineIcon = styled(Icon).attrs((props) => ({
  size: props.size,
  icon: Icons.TURBINE,
  color: props.theme.entityDetails.siteHeaderIconColor,
}))`
  margin-top: ${(props) => (props.isDisconnected ? '4px' : '0')};
`;

const DetailCard = styled.div`
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1);
    display: flex;
    justify-content: space-between;
    padding: 12px 17px;
    border-radius: 5%;
    .back-arrow {
      height: 20px;
      width: 20px;
      padding: 6px;
      border-radius: 50%;
      background-color: ${(props) => props.theme.entityDetails.siteBackButton};  
      position: relative;
      top: 15px;
      line-height: 20px;
    }
    span {
      color: ${(props) => props.theme.entityDetails.siteHeaderIconColor};
    }
  }
  .details-weather{
    flex-direction: row-reverse;    
    color: ${(props) => props.theme.entityDetails.siteHeaderIconColor};
    .weather-data{
      margin-left:6px;
      svg{
        fill: ${(props) => props.theme.entityDetails.siteHeaderIconColor};
      }
    }
  }
`;
const TurbineBadge = styled.div`
  display: flex;
  // margin-left: -23px;
`;
const TurbineSubTitle = styled.div`
  font-family: GE Inspira;
  font-weight: 300;
  font-size: 14px;
  margin: 0 0 10px 0;
  color: ${(props) => props.theme.entityDetails.siteHeaderIconColor};
  letter-spacing: 0;
  display: flex;
  line-height: normal;
  align-items: center;
  svg {
    margin-right: 5px;
  }
`;
const ToggleButton = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  align-items: center;
  width: 100%;
  align-items: center;
  width: 100%;
  padding: 10px 0;
  border-bottom: 1px solid ${globalColors.grey11};
  .button-wrapper {
    background-color: ${(props) => props.theme.entityDetails.toggleButton};
    padding: 1px;
    border-radius: 11px;
    width: 95%;
    display: flex;
    .active-button {
      padding: 7px;
      width: 50%;
      margin: 2px;
      border-radius: 7px;
      color: ${(props) => props.theme.entityDetails.headerIconColor};
      &.active {
        background-color: ${(props) => props.theme.entityDetails.toggleBackground};
      }
    }
  }
`;

const StatusBadge = styled(Badge)`
  z-index: ${elevations.P10};
  left: 19px;
  opacity: ${(props) => (props.isDisconnected ? '0.4' : '')};
`;

const EntityWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: right;
  padding-bottom: ${(props) => (props.isTurbine ? '3px' : '0')};
  padding-top: ${(props) => (props.isTurbine ? '0' : '2px')};
`;
const SiteNameCard = styled.div`
  width: 43%;
`;
const CONDITION = { temp: 0, wind: 0 };
const SiteDetailCard = ({ siteId, assetId, handleHistoryBack, isTurbine, translateFn, activeTab, setActiveTab }) => {
  const { data } = useSiteDetails({ siteId });
  const { site } = data;

  const { data: assetData } = useSiteRealTimeInfo({ siteId, type: EntityType.SITE });
  const { data: assets } = useAssetRealTimeData({ assetId });
  const getAsset = (assets) => (assets && Object.keys(assets).length ? assets : CONDITION);
  let detail = {};

  if (isTurbine) {
    const { name, site, metrics } = assets;
    detail = {
      name: name || '-',
      siteName: site?.name || '-',
      asset: getAsset(metrics?.conditions),
      state: metrics?.state?.value || null,
      timezone: site?.timezone,
      connection: metrics?.connection?.value || null,
    };
  } else
    detail = {
      name: site?.name || '-',
      country: site?.country || '',
      asset: getAsset(assetData.site),
      timezone: site?.timezone,
    };
  const siteTab = isTurbine ? MobileTab.TURBINE : MobileTab.SITE;
  const { state, siteName, name, asset, country, timezone, connection, site: assetTemp } = detail || {};
  const getEventSiteTimeAndZone = useCallback(() => {
    if (!timezone) return '';
    return getDateTimeBasedOnZone(new Date(), timezone).date;
  }, [timezone]);
  return (
    <>
      <DetailCard isTurbine={isTurbine}>
        <span onClick={(e) => handleHistoryBack(e, siteTab)} className="back-arrow">
          <ChevronIcon />
        </span>
        {isTurbine ? (
          <TurbineBadge>
            <StatusBadge
              small
              color={getTurbineStateColor(state)}
              label={getTurbineStateType(state)}
              isDisconnected={connection === AssetConnectionStatus.NOCOMM}
            />
            <TurbineIcon
              className="titleRow"
              size="36"
              isDisconnected={connection === AssetConnectionStatus.NOCOMM}
            />
          </TurbineBadge>
        ) : (
          <SiteIcon className="titleRow" size="36" />
        )}
        <SiteNameCard>
          {isTurbine && (
            <TurbineSubTitle>
              <SiteIcon size={siteName && siteName.length > 30 ? '30' : '15'} />
              {siteName}
            </TurbineSubTitle>
          )}
          <CasePanelSubTitle>{name}</CasePanelSubTitle>
          <span>{country}</span>
          {!isTurbine && (
            <SiteDetails>
              <TurbineIcon size="12" />
              <h5 className="turbine-count">{asset.total || assetData?.assets?.length || '-'}</h5>
              <OfflineIcon />
              <h5>{asset.offline || '-'}</h5>
            </SiteDetails>
          )}
        </SiteNameCard>
        <EntityWrapper isTurbine={isTurbine}>
          <EntityDetailsWeather className="details-weather" weather={asset} />
          <h3>{getEventSiteTimeAndZone()}</h3>
        </EntityWrapper>
      </DetailCard>
      {isTurbine && (
        <ToggleButton>
          <div className="button-wrapper">
            <button className={`active-button ${activeTab === MobileAssetTab.ALARM ? 'active' : ""}`}
              onClick={() => setActiveTab(MobileAssetTab.ALARM)}>
              {translateFn(`general:mobile.active_alarm`, 'Active Alarm')}
            </button>
            <button className={`active-button ${activeTab === MobileAssetTab.LIVE ? 'active' : ""}`}
              onClick={() => setActiveTab(MobileAssetTab.LIVE)}>
              {translateFn(`general:mobile.live_reading`, 'Live Reading')}
            </button>
          </div>
        </ToggleButton>
      )}
    </>
  );
};

SiteDetailCard.propTypes = {
  onChangeHandler: PropTypes.func,
  theme: PropTypes.instanceOf(Object),
  siteId: PropTypes.string,
  handleHistoryBack: PropTypes.func,
  isTurbine: PropTypes.bool,
  assetId: PropTypes.string,
  translateFn: PropTypes.func.isRequired,
  activeTab: PropTypes.string,
  setActiveTab: PropTypes.func,
};

SiteDetailCard.defaultProps = {
  siteId: '',
  assetId: '',
  onChangeHandler: () => null,
  handleHistoryBack: () => null,
  isTurbine: false,
  setActiveTab: () => null,
};

export default withTheme(SiteDetailCard);
